import { CARD_ASPECT } from './styled';
import { GEM_SHAPE } from './gemShape';
import { random, seedOf } from '../random';

export { GEM_SHAPE };

/** Each damage counter is 10 damage, as everyone counted the glass gems that came with the game. */
export const DAMAGE_PER_GEM = 10;

/**
 * A gem's width against the card's: flat glass gems like the game's are about 18 mm across, the card 63 mm wide.
 */
export const GEM_SIZE = 0.28;

/** Where a Pokémon card's artwork is, as fractions of the card: gems go there first. */
const ARTWORK = { left: 0.1, right: 0.9, top: 0.12, bottom: 0.48 };
const WHOLE_CARD = { left: 0.06, right: 0.94, top: 0.05, bottom: 0.95 };

/** How many random places to try for a gem before sweeping the card for one. */
const TRIES = 120;

/** How finely the card is swept for a free spot, in steps across and down. */
const SWEEP = 40;



/**
 * How close a new gem may sit to the ones already down, center to center against a gem's size, loosest last: with a
 * gap while there's room, then just touching, then nudged a little over its neighbors, as on a well-hit real card.
 */
const SPACINGS = [1.06, 1.0, 0.85, 0.72];

/** A spot for a gem in `area`, chosen by `next`. */
const spotIn = (area, across, down, next) => ({
  x: area.left + across / 2 + next() * (area.right - area.left - across),
  y: area.top + down / 2 + next() * (area.bottom - area.top - down),
});

/** Spots across the whole card, top to bottom, for what random tries missed. */
function sweep(across, down) {
  const spots = [];
  for (let row = 0; row <= SWEEP; row++) {
    for (let col = 0; col <= SWEEP; col++) {
      spots.push({
        x: WHOLE_CARD.left + across / 2 + (col / SWEEP) * (WHOLE_CARD.right - WHOLE_CARD.left - across),
        y: WHOLE_CARD.top + down / 2 + (row / SWEEP) * (WHOLE_CARD.bottom - WHOLE_CARD.top - down),
      });
    }
  }
  return spots;
}

/**
 * Where a Pokémon's damage counters sit: one glass gem per 10 damage, scattered like a handful dropped on the card,
 * over the artwork first and onto the rest of the card once the artwork is full. A gem keeps a gap from the others
 * while there's room, and only on a card too full for that does it touch them (SPACINGS). Each gem's place and colour
 * come from the seed and its own number, so a gem stays where it landed as more are added, and the same Pokémon looks
 * the same every time it's drawn.
 *
 * @param {number} damage
 * @param {number} seed for this Pokémon in play (seedOf)
 * @returns {{ size: number, gems: { x: number, y: number, color: 'amber'|'blue' }[] }} the gems' width against the
 *   card's, and each gem's middle as fractions of the card's width and height
 */
export function gemLayout(damage, seed) {
  const count = Math.max(0, Math.floor(damage / DAMAGE_PER_GEM));
  const across = GEM_SIZE;
  const down = (GEM_SIZE / GEM_SHAPE) * CARD_ASPECT;
  const swept = sweep(across, down);
  const gems = [];
  const distance = (gem, spot) => Math.hypot((gem.x - spot.x) / across, (gem.y - spot.y) / down);
  const room = (spot) => Math.min(Infinity, ...gems.map(gem => distance(gem, spot)));

  for (let i = 0; i < count; i++) {
    const next = random(seed + i * 7919);
    const color = next() < 0.5 ? 'amber' : 'blue';
    const tries = [ARTWORK, WHOLE_CARD].flatMap(area => Array.from({ length: TRIES }, () => spotIn(area, across, down, next)));
    let spot = null;
    for (const spacing of SPACINGS) {
      spot = tries.find(candidate => room(candidate) >= spacing) || swept.find(candidate => room(candidate) >= spacing);
      if (spot) {
        break;
      }
    }
    // Fuller than any card gets in play (a Pokémon is knocked out first): the roomiest spot there is.
    if (!spot) {
      spot = swept.reduce((best, candidate) => (room(candidate) > room(best) ? candidate : best));
    }
    gems.push({ ...spot, color });
  }

  return { size: GEM_SIZE, gems };
}

/**
 * The seed for a Pokémon in play, so its gems stay put wherever it goes: on the board and in its menu, and when it
 * retreats, switches or evolves (the server keeps the active and benched Pokémon in different rows, so a row id would
 * change with the spot). It's the card it started as: the Basic under its evolutions, or itself.
 *
 * @param {Map} row the active Pokémon or bench card, with its devolveAttachments (what it evolved from)
 * @param {?Map} card the card on top
 */
export const gemSeed = (row, card) => {
  const under = row && row.get('devolveAttachments') && row.get('devolveAttachments').size
    ? row.get('devolveAttachments').minBy(attachment => attachment.get('id')).get('card')
    : null;
  const basic = under || card;
  return seedOf(`pokemon-${basic ? basic.get('id') : ''}`);
};
